import { useState } from "react";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { SearchSelect } from "../../../shared/components/SearchSelect";
import {
  crearInvitacion, invitarMasivo, reenviarInvitacion, cancelarInvitacion,
} from "../services/portalService";

/**
 * Invitar condóminos por correo.
 *
 * El administrador solo elige la filial y el correo; el residente recibe un
 * link y él mismo pone su usuario, nombre y contraseña (ver
 * features/auth/pages/InvitacionPage.js y apps/core/api/acceso_views.py).
 */

const ESTADO = {
  PENDIENTE: { label: "Pendiente", color: "#f59e0b", icon: "fa-hourglass-half" },
  USADA:     { label: "Aceptada",  color: "#10b981", icon: "fa-circle-check" },
  VENCIDA:   { label: "Vencida",   color: "#6b7280", icon: "fa-clock" },
  CANCELADA: { label: "Cancelada", color: "#ef4444", icon: "fa-ban" },
};

function fecha(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("es-CR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const labelSt = {
  fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
};
const inputSt = {
  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border-card)", background: "var(--bg-input, var(--hover-bg))",
  color: "var(--text-primary)", fontSize: 13, outline: "none",
};

/** Resultado de crear o reenviar: si el correo no salió, el link se puede
 *  copiar y mandar por otro medio. */
function ResultadoEnvio({ resultado }) {
  const [copiado, setCopiado] = useState(false);
  if (!resultado) return null;
  const ok = resultado.enviado;
  async function copiar() {
    try {
      await navigator.clipboard.writeText(resultado.link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* navegador sin permiso de portapapeles */ }
  }
  return (
    <div style={{
      background: ok ? "rgba(16,185,129,0.08)" : "rgba(245,158,11,0.08)",
      border: `1px solid ${ok ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.35)"}`,
      borderLeft: `3px solid ${ok ? "#10b981" : "#f59e0b"}`,
      borderRadius: 8, padding: "11px 14px", fontSize: 13,
      color: ok ? "#10b981" : "#f59e0b",
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, display: "flex", gap: 7, alignItems: "center" }}>
        <i className={`fa-solid ${ok ? "fa-envelope-circle-check" : "fa-triangle-exclamation"}`} />
        {ok
          ? `Invitación enviada a ${resultado.invitacion.email}`
          : "La invitación se creó, pero el correo no salió"}
      </div>
      {!ok && resultado.error && (
        <div style={{ color: "var(--text-secondary)", marginBottom: 8 }}>{resultado.error}</div>
      )}
      <div style={{ color: "var(--text-secondary)", marginBottom: 6 }}>
        {ok
          ? "También podés copiar el enlace y mandarlo por WhatsApp:"
          : "Copiá el enlace y mandáselo al residente por otro medio:"}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input readOnly value={resultado.link} onFocus={(e) => e.target.select()}
          style={{ ...inputSt, fontSize: 12, fontFamily: "monospace", padding: "7px 10px" }} />
        <button type="button" onClick={copiar} style={{
          padding: "0 12px", borderRadius: 8, border: "1px solid var(--border-card)",
          background: "var(--hover-bg)", color: copiado ? "#10b981" : "var(--text-primary)",
          cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
        }}>
          <i className={`fa-solid ${copiado ? "fa-check" : "fa-copy"}`} style={{ marginRight: 5 }} />
          {copiado ? "Copiado" : "Copiar"}
        </button>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
        Vence el {fecha(resultado.invitacion.expira_en)} y sirve una sola vez.
      </div>
    </div>
  );
}

export function InvitarModal({ filiales, emailsFilial = {}, residentes = [], invitaciones = [], onClose, onCreada }) {
  const [modo, setModo] = useState("una"); // "una" | "varias"
  const [filialId, setFilialId] = useState("");
  const [email, setEmail] = useState("");
  const [emailTocado, setEmailTocado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  const opciones = filiales.map((f) => ({
    id: f.id,
    primary: `Filial ${f.idFilial}`,
    secondary: f.propietario_nombre || "",
  }));

  function elegirFilial(id) {
    setFilialId(id);
    setError("");
    // Precarga el correo principal del propietario, salvo que el
    // administrador ya haya escrito otro a mano.
    if (!emailTocado) setEmail(emailsFilial[id] || emailsFilial[String(id)] || "");
  }

  async function enviar() {
    setError("");
    if (!filialId) { setError("Seleccioná la filial."); return; }
    if (!EMAIL_RE.test(email.trim())) { setError("Ingresá un correo válido (sin tildes ni espacios)."); return; }
    setEnviando(true);
    try {
      const r = await crearInvitacion(Number(filialId), email.trim());
      setResultado(r);
      onCreada?.(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  function otra() {
    setResultado(null);
    setFilialId("");
    setEmail("");
    setEmailTocado(false);
  }

  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 16,
        width: modo === "varias" ? "min(860px, 96vw)" : "min(520px, 96vw)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", overflowY: "auto", transition: "width 0.15s",
      }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: "1px solid var(--border-card)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.3)", color: "#f97316",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            }}>
              <i className="fa-solid fa-paper-plane" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>Invitar por correo</div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                El residente elige su usuario y contraseña desde el enlace
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18,
          }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Una filial / varias a la vez */}
        <div style={{ display: "flex", gap: 0, padding: "0 24px", borderBottom: "1px solid var(--border-card)" }}>
          {[
            ["una", "fa-user", "Una filial"],
            ["varias", "fa-users", "Varias filiales"],
          ].map(([v, icon, lbl]) => (
            <button key={v} type="button" onClick={() => setModo(v)} style={{
              background: "none", border: "none", cursor: "pointer", padding: "11px 14px", marginBottom: -1,
              borderBottom: `2px solid ${modo === v ? "#f97316" : "transparent"}`,
              color: modo === v ? "#f97316" : "var(--text-muted)", fontWeight: modo === v ? 700 : 500, fontSize: 13,
            }}>
              <i className={`fa-solid ${icon}`} style={{ marginRight: 7 }} />{lbl}
            </button>
          ))}
        </div>

        {modo === "varias" ? (
          <InvitarMasivo
            filiales={filiales} emailsFilial={emailsFilial}
            residentes={residentes} invitaciones={invitaciones}
            onClose={onClose} onCreada={onCreada}
          />
        ) : (<>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {resultado ? (
            <ResultadoEnvio resultado={resultado} />
          ) : (
            <>
              <div>
                <label style={labelSt}>Filial <span style={{ color: "#f87171" }}>*</span></label>
                <SearchSelect
                  options={opciones}
                  value={filialId}
                  onChange={(id) => elegirFilial(id)}
                  placeholder="Buscar filial o propietario…"
                  icon="fa-building"
                />
              </div>
              <div>
                <label style={labelSt}>Correo del residente <span style={{ color: "#f87171" }}>*</span></label>
                <input
                  type="email" value={email} placeholder="correo@ejemplo.com"
                  onChange={(e) => { setEmail(e.target.value); setEmailTocado(true); setError(""); }}
                  style={inputSt}
                />
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 5 }}>
                  Se precarga el correo principal del propietario; podés cambiarlo (por ejemplo, para un inquilino).
                </div>
              </div>
              <div style={{
                background: "var(--hover-bg)", borderRadius: 8, padding: "10px 12px",
                fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5,
              }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: "#60a5fa" }} />
                El enlace vence en 7 días y sirve una sola vez. Si el residente olvida su contraseña,
                la puede recuperar desde la pantalla de inicio de sesión.
              </div>
            </>
          )}

          {error && (
            <div style={{
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 8, padding: "10px 14px",
              color: "#f87171", fontSize: 13,
            }}>
              <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }} />
              {error}
            </div>
          )}
        </div>

        <div style={{
          padding: "14px 24px 20px", borderTop: "1px solid var(--border-card)",
          display: "flex", gap: 10, justifyContent: "flex-end",
        }}>
          {resultado ? (
            <>
              <button onClick={otra} style={btnSecundario}>
                <i className="fa-solid fa-plus" style={{ marginRight: 6 }} />Invitar a otro
              </button>
              <button onClick={onClose} style={btnPrimario(false)}>Listo</button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={btnSecundario}>Cancelar</button>
              <button onClick={enviar} disabled={enviando} style={btnPrimario(enviando)}>
                {enviando
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
                  : <><i className="fa-solid fa-paper-plane" /> Enviar invitación</>}
              </button>
            </>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}

/* ── Invitación masiva ──────────────────────────────────────────────────────
   Una fila por filial con el correo principal del propietario ya cargado (se
   puede corregir ahí mismo). Vienen marcadas las que tienen correo y todavía
   no tienen acceso ni invitación pendiente. El envío va en lotes de 10 (el
   máximo que acepta el backend por llamada) para mostrar el avance y que
   ninguna petición tarde tanto como para que el servidor la corte. */
const LOTE = 10;
// Sin tildes ni espacios, igual que valida el servidor: un "sofía@..." antes se
// podía marcar y recién fallaba al enviar.
const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

function natural(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function InvitarMasivo({ filiales, emailsFilial, residentes, invitaciones, onClose, onCreada }) {
  const conAcceso = new Set(residentes.map((r) => Number(r.filial_id)).filter(Boolean));
  const pendiente = new Set(invitaciones.filter((i) => i.estado === "PENDIENTE").map((i) => Number(i.filial_id)));

  const [filas, setFilas] = useState(() => [...filiales]
    .sort((a, b) => natural(a.idFilial, b.idFilial))
    .map((f) => ({
      id: f.id,
      filial: f.idFilial,
      propietario: f.propietario_nombre || "",
      email: emailsFilial[f.id] || emailsFilial[String(f.id)] || "",
      estado: conAcceso.has(f.id) ? "acceso" : pendiente.has(f.id) ? "pendiente" : "",
    })));
  const [sel, setSel] = useState(() => new Set(
    filas.filter((f) => !f.estado && EMAIL_RE.test(f.email)).map((f) => f.id)));
  const [buscar, setBuscar] = useState("");
  const [ocultarConAcceso, setOcultarConAcceso] = useState(true);
  const [avance, setAvance] = useState(null);      // { hechos, total } mientras envía
  const [resultados, setResultados] = useState(null);
  const [error, setError] = useState("");

  const q = buscar.trim().toLowerCase();
  const visibles = filas.filter((f) =>
    (!ocultarConAcceso || f.estado !== "acceso")
    && (!q || f.filial.toLowerCase().includes(q) || f.propietario.toLowerCase().includes(q)
        || f.email.toLowerCase().includes(q)));
  const valida = (f) => EMAIL_RE.test(f.email.trim());
  const marcables = visibles.filter(valida);
  const todas = marcables.length > 0 && marcables.every((f) => sel.has(f.id));
  const elegidas = filas.filter((f) => sel.has(f.id) && valida(f));
  const sinCorreo = filas.filter((f) => f.estado !== "acceso" && !f.email.trim()).length;

  function cambiarEmail(id, email) {
    setFilas((fs) => fs.map((f) => (f.id === id ? { ...f, email } : f)));
    // Una fila que recibe un correo válido se marca sola; si lo borran, se desmarca.
    setSel((s) => {
      const n = new Set(s);
      if (EMAIL_RE.test(email.trim())) n.add(id); else n.delete(id);
      return n;
    });
  }
  function toggle(id) {
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleTodas() {
    setSel((s) => {
      const n = new Set(s);
      marcables.forEach((f) => (todas ? n.delete(f.id) : n.add(f.id)));
      return n;
    });
  }

  async function enviar() {
    setError("");
    const items = elegidas.map((f) => ({ filial_id: f.id, email: f.email.trim() }));
    if (!items.length) { setError("Marcá al menos una filial con correo."); return; }
    const acumulado = [];
    setAvance({ hechos: 0, total: items.length });
    for (let i = 0; i < items.length; i += LOTE) {
      const lote = items.slice(i, i + LOTE);
      try {
        const r = await invitarMasivo(lote);
        acumulado.push(...(r.resultados || []));
      } catch (e) {
        // Si un lote entero falla (red, servidor), sus filas quedan como
        // error y se sigue con el resto.
        lote.forEach((it) => acumulado.push({
          ...it, filial: filas.find((f) => f.id === it.filial_id)?.filial ?? "",
          enviado: false, error: e.message, link: null,
        }));
      }
      setAvance({ hechos: Math.min(i + LOTE, items.length), total: items.length });
    }
    setAvance(null);
    setResultados(acumulado);
    onCreada?.();
  }

  // ── Resultado ──
  if (resultados) {
    const ok = resultados.filter((r) => r.enviado);
    const mal = resultados.filter((r) => !r.enviado);
    return (
      <>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Contador color="#10b981" icon="fa-envelope-circle-check" n={ok.length} lbl="Invitaciones enviadas" />
            <Contador color={mal.length ? "#f59e0b" : "#64748b"} icon="fa-triangle-exclamation" n={mal.length} lbl="No se enviaron" />
          </div>
          {mal.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>
                Estas no salieron por correo. Si la invitación se creó, podés copiar el enlace y mandarlo por otro medio:
              </div>
              <div style={{ display: "grid", gap: 6, maxHeight: 300, overflowY: "auto" }}>
                {mal.map((r, i) => <FilaError key={i} r={r} />)}
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: "14px 24px 20px", borderTop: "1px solid var(--border-card)",
          display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnPrimario(false)}>Listo</button>
        </div>
      </>
    );
  }

  const enviando = !!avance;
  return (
    <>
      <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 220px" }}>
            <i className="fa-solid fa-magnifying-glass" style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              fontSize: 12, color: "var(--text-muted)", pointerEvents: "none",
            }} />
            <input value={buscar} onChange={(e) => setBuscar(e.target.value)} disabled={enviando}
              placeholder="Buscar filial, propietario o correo…" autoComplete="off" name="buscar-invitar"
              style={{ ...inputSt, paddingLeft: 30 }} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12,
            color: "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={ocultarConAcceso} onChange={(e) => setOcultarConAcceso(e.target.checked)}
              style={{ accentColor: "#f97316" }} />
            Ocultar las que ya tienen acceso ({filas.filter((f) => f.estado === "acceso").length})
          </label>
        </div>

        {sinCorreo > 0 && (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: "#60a5fa" }} />
            {sinCorreo} filial(es) no tienen correo del propietario registrado: escribilo en su fila para poder invitarla.
          </div>
        )}

        <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ maxHeight: "min(46vh, 420px)", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--hover-bg)", position: "sticky", top: 0, zIndex: 1 }}>
                  <th style={{ ...thSt, width: 36 }}>
                    <input type="checkbox" checked={todas} onChange={toggleTodas} disabled={enviando || !marcables.length}
                      title="Marcar todas las visibles con correo" style={{ accentColor: "#f97316", cursor: "pointer" }} />
                  </th>
                  <th style={thSt}>Filial</th>
                  <th style={thSt}>Correo</th>
                  <th style={{ ...thSt, width: 120 }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>
                    Ninguna filial coincide.
                  </td></tr>
                ) : visibles.map((f) => {
                  const ok = valida(f);
                  const marcada = sel.has(f.id) && ok;
                  return (
                    <tr key={f.id} style={{
                      borderTop: "1px solid var(--border-card)",
                      background: marcada ? "rgba(249,115,22,0.06)" : "transparent",
                    }}>
                      <td style={{ padding: "7px 10px" }}>
                        <input type="checkbox" checked={marcada} disabled={!ok || enviando}
                          onChange={() => toggle(f.id)}
                          title={ok ? "" : "Escribí un correo válido para poder invitarla"}
                          style={{ accentColor: "#f97316", cursor: ok ? "pointer" : "not-allowed" }} />
                      </td>
                      <td style={{ padding: "7px 10px", minWidth: 150 }}>
                        <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>{f.filial}</div>
                        {f.propietario && (
                          <div style={{ fontSize: 11, color: "var(--text-muted)", maxWidth: 220,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.propietario}</div>
                        )}
                      </td>
                      <td style={{ padding: "6px 10px", width: "45%" }}>
                        <input type="email" value={f.email} disabled={enviando}
                          onChange={(e) => cambiarEmail(f.id, e.target.value)}
                          placeholder="Sin correo registrado" autoComplete="off" name={`correo-filial-${f.id}`}
                          style={{
                            ...inputSt, padding: "6px 9px", fontSize: 12,
                            borderColor: f.email && !ok ? "rgba(248,113,113,0.6)" : "var(--border-card)",
                          }} />
                      </td>
                      <td style={{ padding: "7px 10px" }}>
                        {f.estado === "acceso" ? (
                          <Etiqueta color="#10b981" icon="fa-circle-check">Ya tiene acceso</Etiqueta>
                        ) : f.estado === "pendiente" ? (
                          <Etiqueta color="#f59e0b" icon="fa-hourglass-half">Pendiente</Etiqueta>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
          Invitar de nuevo una filial con invitación pendiente deja sin efecto el enlace anterior.
          Cada enlace vence en 7 días y sirve una sola vez.
        </div>

        {enviando && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12,
              color: "var(--text-secondary)", marginBottom: 5 }}>
              <span><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />Enviando invitaciones…</span>
              <span>{avance.hechos} de {avance.total}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "var(--hover-bg)", overflow: "hidden" }}>
              <div style={{ width: `${(avance.hechos / avance.total) * 100}%`, height: "100%",
                background: "#f97316", transition: "width 0.25s" }} />
            </div>
          </div>
        )}

        {error && (
          <div style={{ color: "#f87171", fontSize: 13 }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }} />{error}
          </div>
        )}
      </div>

      <div style={{ padding: "14px 24px 20px", borderTop: "1px solid var(--border-card)",
        display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
        <span style={{ marginRight: "auto", fontSize: 12, color: "var(--text-muted)" }}>
          {elegidas.length} seleccionada(s)
        </span>
        <button onClick={onClose} disabled={enviando} style={btnSecundario}>Cancelar</button>
        <button onClick={enviar} disabled={enviando || !elegidas.length} style={{
          ...btnPrimario(enviando), opacity: !elegidas.length ? 0.5 : 1,
          cursor: enviando || !elegidas.length ? "not-allowed" : "pointer",
        }}>
          {enviando
            ? <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
            : <><i className="fa-solid fa-paper-plane" /> Enviar {elegidas.length} {elegidas.length === 1 ? "invitación" : "invitaciones"}</>}
        </button>
      </div>
    </>
  );
}

const thSt = {
  padding: "9px 10px", textAlign: "left", fontSize: 11, fontWeight: 700,
  color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4,
};

function Etiqueta({ color, icon, children }) {
  return (
    <span style={{
      color, background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 20,
      padding: "2px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: 10 }} />{children}
    </span>
  );
}

function Contador({ color, icon, n, lbl }) {
  return (
    <div style={{ border: `1px solid ${color}40`, background: `${color}12`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color }}>
        <i className={`fa-solid ${icon}`} />
        <span style={{ fontSize: 22, fontWeight: 800 }}>{n}</span>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{lbl}</div>
    </div>
  );
}

function FilaError({ r }) {
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(r.link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { /* sin permiso de portapapeles */ }
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
      borderRadius: 8, background: "var(--hover-bg)", flexWrap: "wrap" }}>
      <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: 13 }}>{r.filial || "-"}</span>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{r.email}</span>
      <span style={{ fontSize: 12, color: "#f59e0b", flex: "1 1 200px" }}>{r.error}</span>
      {r.link && (
        <button type="button" onClick={copiar} style={{
          padding: "4px 10px", borderRadius: 7, border: "1px solid var(--border-card)",
          background: "transparent", color: copiado ? "#10b981" : "var(--text-primary)",
          cursor: "pointer", fontSize: 12, whiteSpace: "nowrap",
        }}>
          <i className={`fa-solid ${copiado ? "fa-check" : "fa-copy"}`} style={{ marginRight: 5 }} />
          {copiado ? "Copiado" : "Copiar enlace"}
        </button>
      )}
    </div>
  );
}

const btnSecundario = {
  padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-card)",
  background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14, fontWeight: 500,
};
const btnPrimario = (busy) => ({
  padding: "9px 20px", borderRadius: 8, border: "none",
  background: busy ? "#b45309" : "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
  cursor: busy ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8,
});

/** Lista de invitaciones enviadas, con reenviar / cancelar. */
export function InvitacionesPanel({ invitaciones, onCambio, showToast }) {
  const [abierto, setAbierto] = useState(true);
  const [ocupado, setOcupado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [verTodas, setVerTodas] = useState(false);

  if (!invitaciones || invitaciones.length === 0) return null;

  const pendientes = invitaciones.filter((i) => i.estado === "PENDIENTE").length;
  const lista = verTodas ? invitaciones : invitaciones.filter((i) => i.estado !== "CANCELADA").slice(0, 8);

  async function reenviar(inv) {
    setOcupado(inv.id);
    setResultado(null);
    try {
      const r = await reenviarInvitacion(inv.id);
      setResultado(r);
      showToast?.(r.enviado ? `Invitación reenviada a ${inv.email}` : "Se generó un enlace nuevo, pero el correo no salió", r.enviado);
      onCambio?.();
    } catch (e) {
      showToast?.(e.message, false);
    } finally {
      setOcupado(null);
    }
  }

  async function cancelar(inv) {
    setOcupado(inv.id);
    try {
      await cancelarInvitacion(inv.id);
      showToast?.("Invitación cancelada", true);
      onCambio?.();
    } catch (e) {
      showToast?.(e.message, false);
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 8, marginBottom: 18, overflow: "hidden",
    }}>
      <button type="button" onClick={() => setAbierto((v) => !v)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", background: "none", border: "none", cursor: "pointer",
        borderBottom: abierto ? "1px solid var(--border-card)" : "none",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-primary)", fontWeight: 700, fontSize: 13 }}>
          <i className="fa-solid fa-paper-plane" style={{ color: "#f97316" }} />
          Invitaciones
          {pendientes > 0 && (
            <span style={{
              background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)",
              color: "#f59e0b", borderRadius: 20, padding: "1px 9px", fontSize: 11, fontWeight: 700,
            }}>
              {pendientes} pendiente{pendientes !== 1 ? "s" : ""}
            </span>
          )}
        </span>
        <i className={`fa-solid fa-chevron-${abierto ? "up" : "down"}`} style={{ color: "var(--text-muted)", fontSize: 12 }} />
      </button>

      {abierto && (
        <div style={{ padding: "8px 12px 12px" }}>
          {resultado && (
            <div style={{ marginBottom: 10 }}>
              <ResultadoEnvio resultado={resultado} />
            </div>
          )}
          <div style={{ display: "grid", gap: 6 }}>
            {lista.map((inv) => {
              const est = ESTADO[inv.estado] || ESTADO.PENDIENTE;
              const reenviable = inv.estado !== "USADA";
              return (
                <div key={inv.id} style={{
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  padding: "9px 10px", borderRadius: 8, background: "var(--hover-bg)",
                }}>
                  <span style={{
                    background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
                    color: "#f97316", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}>
                    Filial {inv.filial}
                  </span>
                  <span style={{ fontSize: 13, color: "var(--text-primary)", flex: "1 1 180px", minWidth: 0,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {inv.email}
                    {inv.usuario && (
                      <span style={{ color: "var(--text-muted)", fontFamily: "monospace", fontSize: 12 }}> · @{inv.usuario}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    {inv.estado === "USADA" ? `Aceptada ${fecha(inv.usada_en)}` : `Vence ${fecha(inv.expira_en)}`}
                  </span>
                  <span style={{
                    color: est.color, background: `${est.color}18`, border: `1px solid ${est.color}40`,
                    borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                  }}>
                    <i className={`fa-solid ${est.icon}`} style={{ fontSize: 10 }} />
                    {est.label}
                  </span>
                  {reenviable && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button type="button" disabled={ocupado === inv.id} onClick={() => reenviar(inv)}
                        title="Genera un enlace nuevo (el anterior deja de servir) y lo vuelve a enviar"
                        style={accionSt("#3b82f6")}>
                        <i className={`fa-solid ${ocupado === inv.id ? "fa-circle-notch fa-spin" : "fa-rotate-right"}`} />
                        {" "}Reenviar
                      </button>
                      {inv.estado === "PENDIENTE" && (
                        <button type="button" disabled={ocupado === inv.id} onClick={() => cancelar(inv)}
                          title="Cancelar invitación" style={accionSt("#ef4444")}>
                          <i className="fa-solid fa-ban" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {invitaciones.length > lista.length || verTodas ? (
            <button type="button" onClick={() => setVerTodas((v) => !v)} style={{
              marginTop: 8, background: "none", border: "none", color: "var(--text-muted)",
              cursor: "pointer", fontSize: 12, textDecoration: "underline",
            }}>
              {verTodas ? "Ver menos" : `Ver todas (${invitaciones.length})`}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

const accionSt = (c) => ({
  padding: "5px 10px", borderRadius: 7, border: `1px solid ${c}4d`, background: `${c}14`,
  color: c, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap",
});
